import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';
import type { PublicCatalogFilterOptions } from '@/features/operations/api';
import {
  chosenValues,
  modelLabel,
  setPriceRange,
  toggleFilterValue,
  toNumericAmount,
  type CatalogMultiFilter,
} from '@/features/operations/catalogQuery';

const VISIBLE_OPTIONS = 6;

type GroupItem = { value: string; label: string; count: number };

function FilterGroup({
  title,
  items,
  selected,
  onToggle,
}: {
  title: string;
  items: GroupItem[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const regionId = useId();
  // A chosen value with no devices left (an old shared link) stays listed so it can be unchecked.
  const stale = selected
    .filter((value) => !items.some((item) => item.value === value))
    .map((value) => ({ value, label: 'No disponible', count: 0 }));
  const all = [...stale, ...items];
  const visible = showAll ? all : all.slice(0, VISIBLE_OPTIONS);

  if (!all.length) return null;

  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={regionId}
        sx={{ width: '100%', justifyContent: 'space-between', py: 1, borderRadius: 1 }}
      >
        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ButtonBase>
      <Collapse in={open} id={regionId}>
        <Stack>
          {visible.map((item) => (
            <FormControlLabel
              key={item.value}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(item.value)}
                  onChange={() => onToggle(item.value)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <span>{item.label}</span>
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({item.count})
                  </Typography>
                </Stack>
              }
              sx={{ mr: 0 }}
            />
          ))}
          {all.length > VISIBLE_OPTIONS ? (
            <Button
              size="small"
              color="inherit"
              onClick={() => setShowAll((value) => !value)}
              sx={{ alignSelf: 'flex-start', textDecoration: 'underline' }}
            >
              {showAll ? 'Mostrar menos' : `Mostrar ${all.length - VISIBLE_OPTIONS} más`}
            </Button>
          ) : null}
        </Stack>
      </Collapse>
    </Box>
  );
}

const usdPattern = /^\d{1,8}(?:[.,]\d{1,2})?$/;

function toUsdParam(value: string): string | null | undefined {
  // A trailing separator ("350,") is a number still being typed, not an error.
  const trimmed = value.trim().replace(/[.,]$/, '');
  if (!trimmed) return null;
  return usdPattern.test(trimmed) ? trimmed.replace(',', '.') : undefined;
}

function PriceFilter({
  params,
  onParamsChange,
}: {
  params: URLSearchParams;
  onParamsChange: (next: URLSearchParams) => void;
}) {
  const urlMin = params.get('minPrice') ?? '';
  const urlMax = params.get('maxPrice') ?? '';
  const [min, setMin] = useState(urlMin);
  const [max, setMax] = useState(urlMax);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMin(urlMin.replace('.', ','));
    setMax(urlMax.replace('.', ','));
    setError(null);
  }, [urlMin, urlMax]);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    const minParam = toUsdParam(min);
    const maxParam = toUsdParam(max);
    if (minParam === undefined || maxParam === undefined) {
      setError('Ingresá un monto en USD, por ejemplo 350 o 349,99.');
      return;
    }
    if (minParam !== null && maxParam !== null && Number(minParam) > Number(maxParam)) {
      setError('El precio mínimo no puede ser mayor que el máximo.');
      return;
    }
    setError(null);
    onParamsChange(setPriceRange(params, minParam, maxParam));
  };

  const field = (label: string, value: string, setValue: (value: string) => void) => (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(event) => setValue(toNumericAmount(event.target.value))}
      error={Boolean(error)}
      inputProps={{ inputMode: 'decimal' }}
      InputLabelProps={{ shrink: true }}
      InputProps={{ startAdornment: <InputAdornment position="start">USD</InputAdornment> }}
    />
  );

  return (
    <Box component="form" onSubmit={apply} noValidate>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, py: 1 }}>
        Precio
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1}>
          {field('Mínimo', min, setMin)}
          {field('Máximo', max, setMax)}
        </Stack>
        {error ? (
          <Typography variant="body2" color="error" role="alert">
            {error}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Se filtra en dólares, aunque también veas el equivalente en pesos.
          </Typography>
        )}
        <Button type="submit" variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
          Aplicar
        </Button>
      </Stack>
    </Box>
  );
}

type CatalogFilterPanelProps = {
  params: URLSearchParams;
  options: PublicCatalogFilterOptions | null;
  onParamsChange: (next: URLSearchParams) => void;
};

/** The filter column on a desktop, and the content of the full-screen panel on a phone. */
export function CatalogFilterPanel({ params, options, onParamsChange }: CatalogFilterPanelProps) {
  const toggle = (name: CatalogMultiFilter) => (value: string) =>
    onParamsChange(toggleFilterValue(params, name, value, options));

  const groups: { name: CatalogMultiFilter; title: string; items: GroupItem[] }[] = [
    {
      name: 'brand',
      title: 'Marca',
      items: (options?.brands ?? []).map((brand) => ({
        value: brand.id,
        label: brand.name,
        count: brand.count,
      })),
    },
    {
      name: 'model',
      title: 'Modelo',
      items: (options?.models ?? []).map((model) => ({
        value: model.id,
        label: modelLabel(model),
        count: model.count,
      })),
    },
    {
      name: 'storage',
      title: 'Capacidad',
      items: (options?.storages ?? []).map((storage) => ({
        value: String(storage.value),
        label: `${storage.value} GB`,
        count: storage.count,
      })),
    },
    {
      name: 'color',
      title: 'Color',
      items: (options?.colors ?? []).map((color) => ({
        value: color.id,
        label: color.name,
        count: color.count,
      })),
    },
  ];

  return (
    <Stack spacing={1} divider={<Divider flexItem />} component="section" aria-label="Filtros">
      {groups.map((group) => (
        <FilterGroup
          key={group.name}
          title={group.title}
          items={group.items}
          selected={chosenValues(params, group.name)}
          onToggle={toggle(group.name)}
        />
      ))}
      <PriceFilter params={params} onParamsChange={onParamsChange} />
    </Stack>
  );
}
